import { BellRing } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import {
  browserPushState,
  disableFirebasePush,
  enableFirebasePush,
  PushSetupError,
  type BrowserPushState,
} from './firebasePush';
import { NotificationToggleConfirm } from './NotificationToggleConfirm';

export function DevicePushPreference({ token }: { token: string }) {
  const t = useI18nStore((state) => state.t);
  const [state, setState] = useState<BrowserPushState>('disabled');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    void browserPushState().then((next) => {
      if (active) setState(next);
    });
    return () => { active = false; };
  }, [token]);

  const toggle = async (nextEnabled: boolean) => {
    if (!token || pending) return;
    setPending(true);
    setMessage('');
    try {
      if (!nextEnabled) {
        await disableFirebasePush(token);
        setState('disabled');
        setMessage(t('pushDisabled'));
      } else {
        await enableFirebasePush(token);
        setState('enabled');
        setMessage(t('pushEnabled'));
      }
    } catch (error) {
      const code = error instanceof PushSetupError ? error.code : String((error as { message?: string })?.message || '');
      const blocked = code === 'PUSH_PERMISSION_BLOCKED' || (typeof Notification !== 'undefined' && Notification.permission === 'denied');
      setState(blocked ? 'blocked' : state);
      setMessage(t(blocked ? 'pushPermissionBlocked' : state === 'unsupported' ? 'pushUnsupported' : 'pushEnableFailed'));
    } finally {
      setPending(false);
      setConfirmTarget(null);
    }
  };

  const enabled = state === 'enabled';
  const unavailable = state === 'unsupported' || state === 'unavailable';
  return (
    <>
      <button className="me-menu-row" type="button" disabled={pending || unavailable} onClick={() => setConfirmTarget(!enabled)}>
        <BellRing size={19} aria-hidden="true" />
        <span>{t('meDeviceNotifications')}</span>
        <b className={enabled ? 'enabled' : ''}>
          {pending ? t('submitting') : enabled ? t('on') : unavailable ? t('pushUnavailable') : t('off')}
        </b>
      </button>
      {message ? <p className="form-success preference-feedback-react">{message}</p> : null}
      {confirmTarget !== null ? (
        <NotificationToggleConfirm
          kind="device"
          nextEnabled={confirmTarget}
          pending={pending}
          onCancel={() => { if (!pending) setConfirmTarget(null); }}
          onConfirm={() => void toggle(confirmTarget)}
        />
      ) : null}
    </>
  );
}
